import React, { useCallback, useEffect, useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Table from '@mui/joy/Table';
import CircularProgress from '@mui/joy/CircularProgress';
import Tooltip from '@mui/joy/Tooltip';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import { listAgents } from '../../api/agent';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

function formatDate(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/** Last path segment of a model id (e.g. `livekit/ultravox` → `ultravox`). */
function modelDisplayName(modelName) {
  if (modelName == null || String(modelName).trim() === '') return '–';
  const s = String(modelName);
  const i = s.lastIndexOf('/');
  return i >= 0 ? s.slice(i + 1) : s;
}

export default function AgentLoadDialog({ open, onClose, onSelect }) {
  const [agents, setAgents] = useState([]);
  /** Next page offset from API (`false` when no more pages). */
  const [nextOffset, setNextOffset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterSearchDebounced, setFilterSearchDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setFilterSearchDebounced(filterSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filterSearch]);

  const loadPage = useCallback(
    async (offset, append) => {
      const isFirst = !append;
      if (isFirst) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const data = await listAgents({
          limit: PAGE_SIZE,
          offset,
          search: filterSearchDebounced || undefined
        });
        const rows = Array.isArray(data?.agents) ? data.agents : [];
        const next = data?.next === false ? null : data?.next;
        setAgents((prev) => (append ? [...prev, ...rows] : rows));
        setNextOffset(next);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Failed to load agents';
        setError(msg);
        if (!append) setAgents([]);
        setNextOffset(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filterSearchDebounced]
  );

  useEffect(() => {
    if (!open) return;
    setFilterSearch('');
    setFilterSearchDebounced('');
    setAgents([]);
    setNextOffset(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    loadPage(0, false);
  }, [open, filterSearchDebounced, loadPage]);

  const handleSelect = (agent) => {
    onSelect(agent.id);
    onClose();
  };

  const hasMore = nextOffset != null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: { xs: 520, sm: 720 }, width: '100%' }}>
        <ModalClose />
        <Typography level="h4" component="h2" sx={{ mb: 0.5 }}>
          Load agent
        </Typography>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Typography level="body2" sx={{ mb: 2, color: 'neutral.600' }}>
            Choose a row to open in the editor. Hover a name for its description; hover the model for the full model id. Use Load more when needed.
          </Typography>
        </Box>

        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', mb: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Search
          </Typography>
          <Input
            size="sm"
            placeholder="Filter by name or model…"
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
          />
        </Sheet>

        {loading && !agents.length && (
          <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size="sm" />
          </Sheet>
        )}
        {error && (
          <Typography level="body2" color="danger" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && agents.length === 0 && (
          <Typography level="body2" color="neutral">
            No agents match your filters. Save an agent first or adjust search.
          </Typography>
        )}
        {agents.length > 0 && (
          <Box sx={{ maxHeight: { xs: 420, sm: 360 }, overflow: 'auto' }}>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <List sx={{ '--List-gap': '0.5rem' }}>
                {agents.map((agent) => (
                  <ListItem key={agent.id}>
                    <ListItemButton onClick={() => handleSelect(agent)} sx={{ borderRadius: 'sm' }}>
                      <ListItemContent>
                        <Typography level="body-sm" fontWeight="lg" noWrap>
                          {agent.name || 'Unnamed agent'}
                        </Typography>
                        <Typography level="body-xs" color="neutral" noWrap>
                          {modelDisplayName(agent.modelName)}
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Table stickyHeader size="sm" sx={{ '--TableCell-paddingX': '0.75rem' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ width: '7.5rem' }}>Model</th>
                    <th style={{ width: '8.5rem' }}>Created</th>
                    <th style={{ width: '8.5rem' }}>Modified</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr
                      key={agent.id}
                      onClick={() => handleSelect(agent)}
                      style={{ cursor: 'pointer' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(agent);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Load agent ${agent.name || agent.id}`}
                    >
                      <td>
                        <Tooltip
                          title={agent.description?.trim() ? agent.description : 'No description'}
                          variant="soft"
                          placement="top-start"
                          sx={{ maxWidth: 360 }}
                        >
                          <Typography level="body-sm" fontWeight="lg" sx={{ width: 'fit-content', maxWidth: '100%' }}>
                            {agent.name || 'Unnamed agent'}
                          </Typography>
                        </Tooltip>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {agent.modelName ? (
                          <Tooltip title={agent.modelName} variant="soft" placement="top">
                            <Typography level="body-sm" component="span">
                              {modelDisplayName(agent.modelName)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          '–'
                        )}
                      </td>
                      <td title={formatDateTime(agent.createdAt)}>{formatDate(agent.createdAt)}</td>
                      <td title={formatDateTime(agent.updatedAt)}>{formatDate(agent.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>
            {hasMore && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="sm"
                  variant="outlined"
                  loading={loadingMore}
                  onClick={() => loadPage(nextOffset, true)}
                >
                  Load more
                </Button>
              </Box>
            )}
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
}
